import type { CustomJSONComponentTypes, GuestSiteComponentProps } from './JsonComponent.types';
import type React from 'react';
import { importWithRetry } from '@shared/core/chunkRetry';
// Only import lightweight constants, not the full configs
import { CASH_FUND_COMPONENT_SCHEMA_URL, CASH_FUND_COMPONENT_TYPE } from '@shared/websiteCustomJsonComponents/CashFundComponent/CashFundComponent.constants';
import { PLACE_TO_STAY_SCHEMA_URL, PLACE_TO_STAY_COMPONENT_TYPE } from '@website-custom-components/PlaceToStay/PlaceToStay.constants';
import { THINGS_TO_DO_SCHEMA_URL, THINGS_TO_DO_COMPONENT_TYPE } from '@website-custom-components/ThingsToDo/ThingsToDo.constants';
import { DEFAULT_THINGS_TO_DO_SCHEMA_URL, DEFAULT_THINGS_TO_DO_COMPONENT_TYPE } from '@website-custom-components/DefaultThingsToDo/DefaultThingsToDo.constants';
import { GENERAL_SCHEDULE_SCHEMA_URL, GENERAL_SCHEDULE_COMPONENT_TYPE } from '@website-custom-components/GeneralSchedule/GeneralSchedule.constants';
import { PAGE_SCHEDULE_SCHEMA_URL, PAGE_SCHEDULE_V1_0_3_SCHEMA_URL, PAGE_SCHEDULE_COMPONENT_TYPE } from '@website-custom-components/PageSchedule/PageSchedule.constants';
import { FORM_DISPLAY_SCHEMA_URL, FORM_DISPLAY_COMPONENT_TYPE } from '@website-custom-components/FormDisplay/FormDisplay.constants';
import { VIP_SCHEMA_URL, VIP_COMPONENT_TYPE } from '@website-custom-components/Vip/Vip.constants';
import { WEATHER_REPORT_SCHEMA_URL, WEATHER_REPORT_COMPONENT_TYPE } from '@website-custom-components/WeatherReport/WeatherReport.constants';
import { FAQ_SCHEMA_URL, FAQ_COMPONENT_TYPE } from '@website-custom-components/Faq/Faq.constants';
import { EVENT_COUNTDOWN_SCHEMA_URL, EVENT_COUNTDOWN_COMPONENT_TYPE } from '@website-custom-components/EventCountdown/EventCountdown.constants';
import { RSVP_BLOCK_SCHEMA_URL, RSVP_BLOCK_COMPONENT_TYPE } from '@website-custom-components/RsvpBlock/RsvpBlock.constants';
import { STAY_BOOKING_ASSISTANT_COMPONENT_TYPE, STAY_BOOKING_ASSISTANT_SCHEMA_URL } from './customComponents/StayBookingAssistant/StayBookingAssistant.constants';
import { GRAPHIC_ACCENT_SCHEMA_URL, GRAPHIC_ACCENT_COMPONENT_TYPE } from '@website-custom-components/GraphicAccent/GraphicAccent.constants';
import { EVENT_DETAILS_SCHEMA_URL, EVENT_DETAILS_COMPONENT_TYPE } from '@website-custom-components/EventDetails/EventDetails.constants';
import { EVENT_DISPLAY_NAME_SCHEMA_URL, EVENT_DISPLAY_NAME_COMPONENT_TYPE } from '@website-custom-components/EventDisplayName/EventDisplayName.constants';
import { GREETING_MESSAGE_SCHEMA_URL, GREETING_MESSAGE_COMPONENT_TYPE } from '@website-custom-components/GreetingMessage/GreetingMessage.constants';
import { TRAVEL_LIST_SCHEMA_URL, TRAVEL_LIST_COMPONENT_TYPE } from '@website-custom-components/TravelList/TravelList.constants';

const CONFIG_CHUNK_NAME = 'content/guest/website/customComponentConfig';

/**
 * Lightweight metadata registry for guest-side custom components.
 * Keyed by schemaUrl -- only loads the component when needed.
 *
 * CHUNKING: every `loadComponent` below deliberately shares ONE webpackChunkName,
 * `content/guest/website/customComponentConfig`. Each config module is a ~6-line
 * re-export of a lazy component, so per-config chunks cost a request each (measured
 * 2026-08-20: 8 requests for ~5 KB br on a section-rich event) and add a hop to the
 * two-step waterfall (config chunk -> component chunk). Merged, the page pays one
 * request for all configs and later widgets skip the first hop. The heavy component
 * chunks stay separate -- this merges only the metadata layer. When adding an entry,
 * use the SAME chunk name; a new one-off name reintroduces the per-widget request.
 *
 * MIGRATION NOTE: When a component adds admin-side migrations (via
 * config.migration), you MUST also register all legacy schemaUrls in
 * this registry pointing to the same loadComponent function. This
 * ensures guest rendering works for data that hasn't been admin-migrated yet.
 *
 * The guest component is responsible for normalizing legacy content
 * shapes if needed (see normalizeWeatherReportContent for an example).
 */
type ComponentMetadata = Readonly<{
  schemaUrl: string;
  componentType: CustomJSONComponentTypes;
  configExportName: string;
  loadComponent: () => Promise<React.ComponentType<GuestSiteComponentProps>>;
  /**
   * Opt in to rendering a blob whose schema version this bundle does not recognise, by falling back
   * to `componentType`. See `resolveComponentMetadata`.
   *
   * Set this only if the current implementation reads EVERY version the component has published.
   * An unrecognised schema is just as likely to be an abandoned older version as a newer one: prod
   * holds one StayBookingAssistant blob on V1_0_0 whose keys are disjoint from V2_0_1's, and
   * rendering it with the V2 implementation would break a component that is currently only missing.
   */
  tolerateUnknownSchemaVersion?: boolean;
}>;

const COMPONENT_REGISTRY: Readonly<Record<string, ComponentMetadata>> = {
  [CASH_FUND_COMPONENT_SCHEMA_URL]: {
    schemaUrl: CASH_FUND_COMPONENT_SCHEMA_URL,
    componentType: CASH_FUND_COMPONENT_TYPE,
    configExportName: 'CashFundComponentGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@apps/guest/routes/CashFunds/websiteEditorComponent/CashFundComponent.guest.config'
      );
      return configModule.CashFundComponentGuestConfig.guestSiteComponent;
    },
  },
  [PLACE_TO_STAY_SCHEMA_URL]: {
    schemaUrl: PLACE_TO_STAY_SCHEMA_URL,
    componentType: PLACE_TO_STAY_COMPONENT_TYPE,
    configExportName: 'PlaceToStayGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/PlaceToStay/PlaceToStay.guest.config');
      return configModule.PlaceToStayGuestConfig.guestSiteComponent;
    },
  },
  [THINGS_TO_DO_SCHEMA_URL]: {
    schemaUrl: THINGS_TO_DO_SCHEMA_URL,
    componentType: THINGS_TO_DO_COMPONENT_TYPE,
    configExportName: 'ThingsToDoGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/ThingsToDo/ThingsToDo.guest.config');
      return configModule.ThingsToDoGuestConfig.guestSiteComponent;
    },
  },
  [DEFAULT_THINGS_TO_DO_SCHEMA_URL]: {
    schemaUrl: DEFAULT_THINGS_TO_DO_SCHEMA_URL,
    componentType: DEFAULT_THINGS_TO_DO_COMPONENT_TYPE,
    configExportName: 'DefaultThingsToDoGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/DefaultThingsToDo/DefaultThingsToDo.guest.config'
      );
      return configModule.DefaultThingsToDoGuestConfig.guestSiteComponent;
    },
  },
  [GENERAL_SCHEDULE_SCHEMA_URL]: {
    schemaUrl: GENERAL_SCHEDULE_SCHEMA_URL,
    componentType: GENERAL_SCHEDULE_COMPONENT_TYPE,
    configExportName: 'GeneralScheduleGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/GeneralSchedule/GeneralSchedule.guest.config'
      );
      return configModule.GeneralScheduleGuestConfig.guestSiteComponent;
    },
  },
  [PAGE_SCHEDULE_SCHEMA_URL]: {
    schemaUrl: PAGE_SCHEDULE_SCHEMA_URL,
    componentType: PAGE_SCHEDULE_COMPONENT_TYPE,
    configExportName: 'PageScheduleGuestConfig',
    // Every published version is additive and readable by the current implementation: v1_0_4 only
    // adds the optional `showWhereToStay` item field on top of v1_0_3.
    tolerateUnknownSchemaVersion: true,
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/PageSchedule/PageSchedule.guest.config');
      return configModule.PageScheduleGuestConfig.guestSiteComponent;
    },
  },
  // An Agenda only re-stamps to v1_0_4 when an admin next opens it, so blobs still naming v1_0_3
  // must keep rendering for guests in the meantime. Same loader, so the same webpack chunk.
  [PAGE_SCHEDULE_V1_0_3_SCHEMA_URL]: {
    schemaUrl: PAGE_SCHEDULE_V1_0_3_SCHEMA_URL,
    componentType: PAGE_SCHEDULE_COMPONENT_TYPE,
    configExportName: 'PageScheduleGuestConfig',
    tolerateUnknownSchemaVersion: true,
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/PageSchedule/PageSchedule.guest.config');
      return configModule.PageScheduleGuestConfig.guestSiteComponent;
    },
  },
  [FORM_DISPLAY_SCHEMA_URL]: {
    schemaUrl: FORM_DISPLAY_SCHEMA_URL,
    componentType: FORM_DISPLAY_COMPONENT_TYPE,
    configExportName: 'FormDisplayGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/FormDisplay/FormDisplay.guest.config');
      return configModule.FormDisplayGuestConfig.guestSiteComponent;
    },
  },
  [VIP_SCHEMA_URL]: {
    schemaUrl: VIP_SCHEMA_URL,
    componentType: VIP_COMPONENT_TYPE,
    configExportName: 'VipGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/Vip/Vip.guest.config');
      return configModule.VipGuestConfig.guestSiteComponent;
    },
  },
  [WEATHER_REPORT_SCHEMA_URL]: {
    schemaUrl: WEATHER_REPORT_SCHEMA_URL,
    componentType: WEATHER_REPORT_COMPONENT_TYPE,
    configExportName: 'WeatherReportGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/WeatherReport/WeatherReport.guest.config'
      );
      return configModule.WeatherReportGuestConfig.guestSiteComponent;
    },
  },
  [FAQ_SCHEMA_URL]: {
    schemaUrl: FAQ_SCHEMA_URL,
    componentType: FAQ_COMPONENT_TYPE,
    configExportName: 'FaqGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/Faq/Faq.guest.config');
      return configModule.FaqGuestConfig.guestSiteComponent;
    },
  },
  [EVENT_COUNTDOWN_SCHEMA_URL]: {
    schemaUrl: EVENT_COUNTDOWN_SCHEMA_URL,
    componentType: EVENT_COUNTDOWN_COMPONENT_TYPE,
    configExportName: 'EventCountdownGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/EventCountdown/EventCountdown.guest.config'
      );
      return configModule.EventCountdownGuestConfig.guestSiteComponent;
    },
  },
  [RSVP_BLOCK_SCHEMA_URL]: {
    schemaUrl: RSVP_BLOCK_SCHEMA_URL,
    componentType: RSVP_BLOCK_COMPONENT_TYPE,
    configExportName: 'RsvpBlockGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/RsvpBlock/RsvpBlock.guest.config');
      return configModule.RsvpBlockGuestConfig.guestSiteComponent;
    },
  },
  [STAY_BOOKING_ASSISTANT_SCHEMA_URL]: {
    schemaUrl: STAY_BOOKING_ASSISTANT_SCHEMA_URL,
    componentType: STAY_BOOKING_ASSISTANT_COMPONENT_TYPE,
    configExportName: 'StayBookingAssistantGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/StayBookingAssistant/StayBookingAssistant.guest.config'
      );
      return configModule.StayBookingAssistantGuestConfig.guestSiteComponent;
    },
  },
  [GRAPHIC_ACCENT_SCHEMA_URL]: {
    schemaUrl: GRAPHIC_ACCENT_SCHEMA_URL,
    componentType: GRAPHIC_ACCENT_COMPONENT_TYPE,
    configExportName: 'GraphicAccentGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/GraphicAccent/GraphicAccent.guest.config'
      );
      return configModule.GraphicAccentGuestConfig.guestSiteComponent;
    },
  },
  [EVENT_DETAILS_SCHEMA_URL]: {
    schemaUrl: EVENT_DETAILS_SCHEMA_URL,
    componentType: EVENT_DETAILS_COMPONENT_TYPE,
    configExportName: 'EventDetailsGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/EventDetails/EventDetails.guest.config');
      return configModule.EventDetailsGuestConfig.guestSiteComponent;
    },
  },
  [EVENT_DISPLAY_NAME_SCHEMA_URL]: {
    schemaUrl: EVENT_DISPLAY_NAME_SCHEMA_URL,
    componentType: EVENT_DISPLAY_NAME_COMPONENT_TYPE,
    configExportName: 'EventDisplayNameGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/EventDisplayName/EventDisplayName.guest.config'
      );
      return configModule.EventDisplayNameGuestConfig.guestSiteComponent;
    },
  },
  [GREETING_MESSAGE_SCHEMA_URL]: {
    schemaUrl: GREETING_MESSAGE_SCHEMA_URL,
    componentType: GREETING_MESSAGE_COMPONENT_TYPE,
    configExportName: 'GreetingMessageGuestConfig',
    loadComponent: async () => {
      const configModule = await import(
        /* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/GreetingMessage/GreetingMessage.guest.config'
      );
      return configModule.GreetingMessageGuestConfig.guestSiteComponent;
    },
  },
  [TRAVEL_LIST_SCHEMA_URL]: {
    schemaUrl: TRAVEL_LIST_SCHEMA_URL,
    componentType: TRAVEL_LIST_COMPONENT_TYPE,
    configExportName: 'TravelListGuestConfig',
    loadComponent: async () => {
      const configModule = await import(/* webpackChunkName: "content/guest/website/customComponentConfig" */ '@website-custom-components/TravelList/TravelList.guest.config');
      return configModule.TravelListGuestConfig.guestSiteComponent;
    },
  },
} as const;

/**
 * Secondary index, component type -> metadata, over the entries that opted in via
 * `tolerateUnknownSchemaVersion`.
 *
 * Several entries share a type only when they are versions of ONE component — the current schema
 * URL plus every legacy one — and those entries carry the same `loadComponent`, so collapsing them
 * is lossless and first-entry-wins is safe. Two different components cannot collide here: each
 * owns a distinct `*_COMPONENT_TYPE` constant.
 */
const REGISTRY_BY_COMPONENT_TYPE: Readonly<Record<string, ComponentMetadata>> = Object.values(COMPONENT_REGISTRY).reduce<Record<string, ComponentMetadata>>((acc, metadata) => {
  if (metadata.tolerateUnknownSchemaVersion && !(metadata.componentType in acc)) {
    acc[metadata.componentType] = metadata;
  }
  return acc;
}, {});

/**
 * Resolves a stored blob to the metadata that should render it.
 *
 * By schemaUrl first, which is every blob written by this bundle or an older one.
 *
 * The type fallback covers the reverse skew, where the DATA is newer than the CODE. This registry
 * only knows the schema versions that shipped in the running bundle, so as soon as a component
 * bumps its version, a blob an admin has already re-stamped matches no key here on any older
 * bundle — mid-deploy, on a bundle a guest has cached, or after a rollback — and the section
 * silently disappears from the couple's live site.
 *
 * Limited to entries that set `tolerateUnknownSchemaVersion`, because an unrecognised schema is not
 * necessarily a newer one — it is just as likely to be an abandoned older version the current
 * implementation cannot read. Consulted only after the schemaUrl lookup comes back empty, so no
 * blob that resolves today resolves differently, and a blob that resolves to nothing today only
 * starts rendering for a component whose author opted in.
 */
const resolveComponentMetadata = (schemaUrl: string | null | undefined, componentType: string | null | undefined): ComponentMetadata | undefined => {
  const bySchema = schemaUrl ? COMPONENT_REGISTRY[schemaUrl] : undefined;
  if (bySchema) {
    return bySchema;
  }
  return componentType ? REGISTRY_BY_COMPONENT_TYPE[componentType] : undefined;
};

/**
 * Lookup function to get guest component by schemaUrl.
 * Only loads the component when actually needed.
 * @param schemaUrl - The schema URL to look up
 * @param componentType - The container's component type, used only when the schema URL is
 *                        unrecognised. See `resolveComponentMetadata`.
 * @returns Promise resolving to the component, or undefined if not found
 */
export const getGuestComponent = async (schemaUrl: string | null | undefined, componentType?: string | null): Promise<React.ComponentType<GuestSiteComponentProps> | undefined> => {
  const metadata = resolveComponentMetadata(schemaUrl, componentType);
  if (!metadata) {
    return undefined;
  }

  // Dynamically import and extract the component only when needed
  return await importWithRetry(metadata.loadComponent, CONFIG_CHUNK_NAME);
};

/**
 * Check if a schemaUrl is registered (without loading the config).
 * Useful for quick lookups to determine if a component type is available.
 * Takes the same optional `componentType` fallback as `getGuestComponent`, so the two cannot
 * disagree about whether a blob is renderable.
 */
export const isGuestComponentRegistered = (schemaUrl: string | null | undefined, componentType?: string | null): boolean => {
  return !!resolveComponentMetadata(schemaUrl, componentType);
};
